import * as THREE from "three";
import type { AssetPipeline } from "../core/assets";
import { FIELD_LAYOUTS, ROAD_LAYOUTS } from "./layout";
import { addHorizonRing } from "./horizon";
import { addWorldRichness } from "./richness";
import { addSettlementStructures } from "./structures";
import { paintFarFieldVertexColors, paintGroundVertexColors, TERRAIN_MATERIALS as MAT } from "./terrain-materials";
import { addVegetation } from "./vegetation";

export type TerrainOptions = {
  size: number;
  segments: number;
};

type Rng = { next: () => number };
type TerrainHeightProvider = (x: number, z: number) => number;

let activeTerrainHeightProvider: TerrainHeightProvider = currentMapTerrainHeightAt;

export function setTerrainHeightProvider(provider: TerrainHeightProvider): void {
  activeTerrainHeightProvider = provider;
}
function seededRng(seed: number): Rng {
  let s = seed | 0;
  return {
    next(): number {
      s = Math.imul(s, 1664525) + 1013904223 | 0;
      return (s >>> 0) / 0xffffffff;
    },
  };
}

// ── Terrain height noise ───────────────────────────────────────────────────
const _perm = new Uint8Array(512);
(function buildPerm() {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let s = 0x12345678;
  for (let i = 255; i > 0; i--) {
    s = Math.imul(s, 1664525) + 1013904223 | 0;
    const j = (s >>> 0) % (i + 1);
    [p[i], p[j]] = [p[j]!, p[i]!];
  }
  for (let i = 0; i < 512; i++) _perm[i] = p[i & 255]!;
})();

function fade(t: number) { return t * t * t * (t * (t * 6 - 15) + 10); }
function grad(hash: number, x: number, y: number): number {
  const h = hash & 7;
  const u = h < 4 ? x : y;
  const v = h < 4 ? y : x;
  return ((h & 1) ? -u : u) + ((h & 2) ? -v : v);
}
function perlin2(x: number, y: number): number {
  const xi = Math.floor(x) & 255, yi = Math.floor(y) & 255;
  const xf = x - Math.floor(x), yf = y - Math.floor(y);
  const u = fade(xf), v = fade(yf);
  const aa = _perm[_perm[xi]! + yi]!, ab = _perm[_perm[xi]! + yi + 1]!;
  const ba = _perm[_perm[xi + 1]! + yi]!, bb = _perm[_perm[xi + 1]! + yi + 1]!;
  return (
    (1 + (
      lerp2(
        lerp2(grad(aa, xf, yf), grad(ba, xf - 1, yf), u),
        lerp2(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u),
        v,
      )
    )) / 2
  );
}
function lerp2(a: number, b: number, t: number) { return a + (b - a) * t; }

function rawTerrainHeightAt(x: number, z: number): number {
  const flatR = 220;
  const dist = Math.sqrt(x * x + z * z);
  const blend = Math.max(0, Math.min(1, (dist - flatR) / 120));
  let h = 0, amp = 8, freq = 0.0028;
  for (let i = 0; i < 4; i++) {
    h += (perlin2(x * freq, z * freq) * 2 - 1) * amp;
    amp *= 0.45; freq *= 2.1;
  }
  return h * blend;
}

function distanceToSegment2D(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): { distance: number; t: number } {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  const cx = ax + abx * t;
  const cz = az + abz * t;
  return { distance: Math.hypot(px - cx, pz - cz), t };
}

function shapedRoadHeight(baseHeight: number, x: number, z: number): number {
  let height = baseHeight;
  for (const road of ROAD_LAYOUTS) {
    for (let i = 0; i < road.points.length - 1; i++) {
      const a = road.points[i]!;
      const b = road.points[i + 1]!;
      const { distance, t } = distanceToSegment2D(x, z, a.x, a.z, b.x, b.z);
      const halfWidth = road.width * 0.5;
      const shoulder = road.width * 0.75;
      if (distance > shoulder) continue;

      const roadStart = rawTerrainHeightAt(a.x, a.z);
      const roadEnd = rawTerrainHeightAt(b.x, b.z);
      const roadHeight = THREE.MathUtils.lerp(roadStart, roadEnd, t);
      const blend = THREE.MathUtils.smoothstep(shoulder, halfWidth * 0.35, distance);
      height = THREE.MathUtils.lerp(height, roadHeight + 0.02, blend);
    }
  }
  return height;
}

function shapedFieldHeight(baseHeight: number, x: number, z: number): number {
  let height = baseHeight;
  for (const field of FIELD_LAYOUTS) {
    const cos = Math.cos(field.rotation);
    const sin = Math.sin(field.rotation);
    const lx = (x - field.x) * cos + (z - field.z) * sin;
    const lz = -(x - field.x) * sin + (z - field.z) * cos;

    const hx = field.width * 0.5;
    const hz = field.depth * 0.5;
    if (Math.abs(lx) > hx || Math.abs(lz) > hz) continue;

    const cornerHeights = [
      rawTerrainHeightAt(field.x - hx * cos + hz * sin, field.z - hx * sin - hz * cos),
      rawTerrainHeightAt(field.x + hx * cos + hz * sin, field.z + hx * sin - hz * cos),
      rawTerrainHeightAt(field.x - hx * cos - hz * sin, field.z - hx * sin + hz * cos),
      rawTerrainHeightAt(field.x + hx * cos - hz * sin, field.z + hx * sin + hz * cos),
    ];
    const fieldHeight = (cornerHeights[0] + cornerHeights[1] + cornerHeights[2] + cornerHeights[3]) / 4;
    const edge = Math.max(Math.abs(lx) / hx, Math.abs(lz) / hz);
    const blend = THREE.MathUtils.smoothstep(1, 0.72, edge);
    height = THREE.MathUtils.lerp(height, fieldHeight + 0.01, blend * 0.85);
  }
  return height;
}

function currentMapTerrainHeightAt(x: number, z: number): number {
  const base = rawTerrainHeightAt(x, z);
  return shapedFieldHeight(shapedRoadHeight(base, x, z), x, z);
}

export function terrainHeightAt(x: number, z: number): number {
  return activeTerrainHeightProvider(x, z);
}

export class Terrain {
  readonly group = new THREE.Group();

  constructor(opts: TerrainOptions = { size: 2000, segments: 100 }, assets?: AssetPipeline) {
    setTerrainHeightProvider(currentMapTerrainHeightAt);
    const { size } = opts;
    const rng = seededRng(0xdeadbeef);

    // Base ground plane with height displacement + vertex color paint
    const segs = 128;
    const groundGeo = new THREE.PlaneGeometry(size, size, segs, segs);
    groundGeo.rotateX(-Math.PI / 2);
    const pos = groundGeo.attributes["position"] as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), z = pos.getZ(i);
      pos.setY(i, terrainHeightAt(x, z));
    }
    pos.needsUpdate = true;
    groundGeo.computeVertexNormals();
    paintGroundVertexColors(groundGeo, size);
    const ground = new THREE.Mesh(groundGeo, MAT.ground);
    this.group.add(ground);

    addSettlementStructures(this.group, rng, size, terrainHeightAt, assets);
    addWorldRichness(this.group, terrainHeightAt, assets);
    this.addFarField(size);
    addHorizonRing(this.group, size, { ridge: 0x5b6a62, haze: 0xaeb6b0 });
    this.addWater(size);
    addVegetation(this.group, rng, size, terrainHeightAt);
  }

  private addWater(size: number): void {
    const river = new THREE.Mesh(new THREE.PlaneGeometry(30, size * 0.65), MAT.water);
    river.rotation.x = -Math.PI / 2;
    river.rotation.z = 0.15;
    river.position.set(size * 0.25, 0.01, -size * 0.04);
    river.userData["losTransparent"] = true; // cosmetic surface — never occludes infantry LOS
    this.group.add(river);
  }

  private addFarField(size: number): void {
    const radius = size * 1.55;
    const geo = new THREE.CircleGeometry(radius, 64);
    geo.rotateX(-Math.PI / 2);

    const inner = size * 0.48;
    paintFarFieldVertexColors(geo, inner);

    const disc = new THREE.Mesh(geo, MAT.farField);
    disc.userData["ignoreBallistics"] = true;
    disc.userData["losTransparent"] = true;
    disc.receiveShadow = true;
    this.group.add(disc);
  }
}
